import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScreenContext, PlanData } from "../Layout";
import { Link, useParams } from "react-router-dom";
import { useSessionStorage } from "react-use";
import * as rudderanalytics from "../../lib/rudderstack";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useTimer } from "react-timer-hook";
import { Button, CustomFlowbiteTheme, Flowbite, Modal } from "flowbite-react";

function TimerBox({text}: {text?: string}) {
  const time = new Date();
  time.setSeconds(time.getSeconds() + 833);

  const { seconds, minutes, hours } = useTimer({ expiryTimestamp: time });

  return (
    <div>
      <div className="inline-block bg-red-600 p-2 font-bold text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="inline-block h-6 w-6 pr-1"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {hours.toString().padStart(2, "0")}:
        {minutes.toString().padStart(2, "0")}:
        {seconds.toString().padStart(2, "0")}
      </div>
      {text ? <p>{text}</p> : <p>Oferta por tempo limitado</p>}
    </div>
  );
}

const OpenIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 15.75l7.5-7.5 7.5 7.5"
    />
  </svg>
);

const converObjectToParams = (object: any) => {
  return Object.keys(object)
    .filter((key) => object[key])
    .map((key) => {
      return `${key}=${encodeURIComponent(object[key])}`;
    })
    .join("&");
};

interface PopupCheckoutProps {
  checkoutLink: any,
  modalState: [boolean, React.Dispatch<React.SetStateAction<boolean>>],
  bestPriceState: [boolean, React.Dispatch<React.SetStateAction<boolean>>],
  selectedPlan: string,
  locale: string
}

const PopupCheckout: React.FC<PopupCheckoutProps> = ({
  checkoutLink,
  modalState,
  bestPriceState,
  selectedPlan,
  locale
}) => {
  const [openModal, setOpenModal] = modalState;
  const [bestPrice, setBestPrice] = bestPriceState;
  const [showBestPrice, setShowBestPrice] = useState<boolean>(true);

  const modalTitle = bestPrice ? "Survey" : "Checkout"

  const customTheme: CustomFlowbiteTheme = {
    modal: {
      show: {
        "on": "flex bg-gray-900 bg-opacity-50 backdrop-blur dark:bg-opacity-80 h-[100%]",
        "off": "hidden"
      },
      header: {
        base: "flex flex-row-reverse items-center border-b-0 rounded-t p-2 dark:border-gray-600",
        popup: "border-b-0 p-2",
        title: "w-full text-md font-normal text-center text-gray-900 dark:text-white",
        close: {
          base: "inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white",
          icon: "h-5 w-5"
        }
      },
      body: {
        base: ""
      },
      footer: {
        base: "flex flex-col border-t-0 p-4 gap-4 bg-gray-100 rounded-b-xl",
      }
    }
  };

  const updateIframeSrc = (src: string) => {
    const iframe = document.getElementById("iframe_root") as HTMLIFrameElement;
    if (iframe) {
      iframe.setAttribute("src", src);
      iframe.onload = () => console.log("iframe loaded");
    }
  };

  useEffect(() => {
    let checkoutLinkSelected = checkoutLink[locale];
    if (selectedPlan === "monthly") {
      checkoutLinkSelected = checkoutLink.monthly;
    }
    if (bestPrice && showBestPrice) {
      checkoutLinkSelected = checkoutLink.bestPrice;
      setShowBestPrice(false);
      setOpenModal(true);
    }
    updateIframeSrc(checkoutLinkSelected);
  }, [bestPrice, selectedPlan]);

  useEffect(() => {
    if (!bestPrice) {
      updateIframeSrc(checkoutLink[locale]);
    }
  }, [locale, bestPrice, checkoutLink]);

  useEffect(() => {
    document.body.style.overflow = openModal ? "hidden" : "auto";
  }, [openModal]);

  return (
    <>
      <Flowbite theme={{ theme: customTheme }}>
        <Modal show={openModal} onClose={() => {setBestPrice(true); setOpenModal(false)}}>
          <Modal.Header>{modalTitle}</Modal.Header>
          <Modal.Body>
          <iframe
            src={checkoutLink[locale]}
            id="iframe_root"
            className="w-full h-full rounded-b-xl"
            style={{ height: "calc(100vh - 100px)" }}
          />
          </Modal.Body>
        </Modal>
      </Flowbite>
    </>
  );
};

function SelectPlan() {
  const { t, i18n } = useTranslation();
  const [screenData, setScreenData] = useScreenContext();
  const [showOthersPlans, setShowOthersPlans] = useState<boolean>(false);
  const [selectedPlan, setselectedPlan] = useState<string>("semiannual");
  const [currentFaq, setCurrentFaq] = useState<number>(0);

  const bestPriceState = useState<boolean>(false);
  const [bestPrice, setBestPrice] = bestPriceState;
  const modalState = useState(false);
  const [showModal, setShowModal] = modalState;

  const { offerType } = useParams();
  const [flowStrategy, setFlowStrategy] = useSessionStorage(
    "flow-strategy",
    ""
  );
  const isRet = sessionStorage.getItem("utm_source") === "ret";
  const isFromTwitter = sessionStorage.getItem("utm_source") === "twitter";

  // const newPricing = useFeatureIsOn("yearly-plan-pricing-feature");
  const removeMonthly = useFeatureIsOn("remove-monthly-plan-feature");
  const newHeadline = useFeatureIsOn("select-plan-headline-feature")
  // const checkout12minPremium = useFeatureIsOn("checkout-12min-premium-feature");
  const exp16Checkout = useFeatureIsOn("exp-16-checkout-feature");
  const exp17feature = useFeatureIsOn("exp-17-feature");
  const newPricing = exp17feature;
  const exp22feature = useFeatureIsOn("exp-22-billet-feature");
  const exp29feature = useFeatureIsOn("exp-29-product");

  const isPostLeadFlowStrategy = flowStrategy === "lead-post-plan-selection";
  const [variant, _] = useSessionStorage("variant");

  const isDirectSale = offerType === "direct";
  const isRetFunnel = offerType === "ret";
  const isLtFunnel = offerType === "lt" || offerType === "ltf";
  const isYtFunnel = offerType === "yt";
  const isTwFunnel = offerType === "tw";
  const isAppFunnel = offerType === "app" || offerType === "appf";

  const isWithInterestVariant = variant === "with-interest" && isDirectSale;

  const activePlan = (isDirectSale || isRetFunnel) && selectedPlan === "semiannual";

  const esPopup = screenData.co === "co" || screenData.co === "mx" || screenData.co === "pe";

  type Funnel = "direct" | "ret" | "lt" | "ltf" | "yt" | "tw";

  const i18nPrefix = {
    direct: "direct_sale_",
    ret: "ret_sale_",
    lt: "lifetime_sale_",
    ltf: "lifetime_sale_",
    yt: "youtube_sale_",
    tw: "twitter_sale_",
    app: "app_sale_",
  } as Record<Funnel, string>;

  const transPrefix = i18nPrefix[offerType as Funnel] || "";

  const checkoutIdListBR: Record<string, string> = {
    direct: "E91856682Y",
    ret: "E91856682Y",
    lt: "L49428651O",
    ltf: "L49428651O",
    yt: "E91856682Y",
    tw: "E91856682Y",
    trial: "P5327674P",
  };

  const checkoutIdListMonthlyBR: Record<string, string> = {
    direct: "P5327674P",
    ret: "P5327674P",
    lt: "L49428651O",
    ltf: "L49428651O",
    yt: "P5327674P",
    tw: "P5327674P",
    trial: "P5327674P",
  };

  // activePlan ? "E91856682Y" : 
  //       isLtFunnel ? "L49428651O" : "P5327674P",
  const checkoutId: any = {
    br: selectedPlan === "semiannual" && checkoutIdListBR[offerType || ""] || checkoutIdListBR.trial,
    en: "K16542811H",
    es: activePlan ? "Y92363487F" : "Y16613490E",
  }[i18n.language];

  const offerCodeList: Record<string, string> = {
    twitter: "xrnblfqt",
    default: "7d18zm3s",
    exp16: "6reyasj1",
    exp17: "6reyasj1",
  };

  const offerCodeDirect: () => string = () => {
    if (isFromTwitter) return offerCodeList.twitter;
    if (exp16Checkout) return offerCodeList.exp16;
    return offerCodeList.default;
  };

  const discount = isRetFunnel  
    ? "promo75off" 
    : (activePlan 
        ? "promo55off" 
        : "promo63off"
      );

  const offersTrial: any = {
    discounts: {
      base: "63OFF7D",
    },
    P5327674P: {
      yearly: { offerCode: "2zki1nl0" },
      monthly: { offerCode: "jx5nh72y" },
    },
    K16542811H: {
      yearly: { offerCode: "z2y60k16" },
      monthly: { offerCode: "fys1w0n7" },
    },
    Y92363487F: {
      yearly: { offerCode: "vng1dkxz" },
      monthly: { offerCode: "7lcaoc5b" },
    },
    Y16613490E: {
      yearly: { offerCode: "vng1dkxz" },
      monthly: { offerCode: "eptks9rt" },
    },
  };

  type Co = "co" | "mx" | "pe" | "ve" | "py" | "ar" | "hn" | "cl";

  const offersDirectSale: any = {
    discounts: {
      base: discount,
    },
    P5327674P: {
      yearly: { offerCode: "suxzbi7s" },
      monthly: { offerCode: "jx5nh72y" },
    },
    E91856682Y: {
      yearly: { offerCode: offerCodeDirect() },
      semiannual: { offerCode: "gxzkdkbo" },
      monthly: { offerCode: "kppi78af" },
    },
    K16542811H: {
      yearly: { offerCode: "evc05pnv" },
      monthly: { offerCode: "b3i96d7z" },
    },
    Y16613490E: {
      yearly: { offerCode: "oqvwtnza" },
      monthly: { offerCode: "eptks9rt" },
    },
    Y92363487F: {
      yearly: { offerCode: "98fq4cvr" },
      monthly: { offerCode: "prlt2kyv" },

      co: {
        yearly: { offerCode: "2earfcgt" },
        monthly: { offerCode: "an79xa2b" },
      },
      mx: {
        yearly: { offerCode: "hdg8agyx" },
        monthly: { offerCode: "eptks9rt" },
      },
      pe: {
        yearly: { offerCode: "f2q21x2l" },
        monthly: { offerCode: "kpzyvt39" },
      },
      ar: {
        yearly: { offerCode: "pk09dmu5" },
        monthly: { offerCode: "v4kfjdja" },
      },
      cl: {
        yearly: { offerCode: "ck7gszd7" },
        monthly: { offerCode: "8jltpjdm" },
      },
    },
  };

  const offersRetSale: any = {
    discounts: {
      base: "promo75off",
    },
    E91856682Y: {
      yearly: { offerCode: "7d18zm3s" },
      monthly: { offerCode: "kppi78af" },
    },
    P5327674P: {
      yearly: { offerCode: "2zki1nl0" },
      monthly: { offerCode: "jx5nh72y" },
    },
  };

  const offersLifetime: any = {
    discounts: {
      base: "",
    },
    L49428651O: {
      yearly: { offerCode: "atfd8gmh" },
    },
  };

  const offersYtSale: any = {
    discounts: {
      base: "promo75off",
    },
    E91856682Y: {
      yearly: { offerCode: "v99ighqd" },
      monthly: { offerCode: "kppi78af" },
    },
    P5327674P: {
      yearly: { offerCode: "2zki1nl0" },
      monthly: { offerCode: "fbixgxyd" },
    },
  };

  const offersTwSale: any = {
    discounts: {
      base: "promo63off",
    },
    E91856682Y: {
      yearly: { offerCode: "i6rm22xe" },
      monthly: { offerCode: "rjkuz3j9" },
    },
    P5327674P: {
      yearly: { offerCode: "2zki1nl0" },
      monthly: { offerCode: "rjkuz3j9" },
    },
  };

  const offersOptions: any = () => {
    if (isDirectSale) return offersDirectSale;
    if (isRetFunnel) return offersRetSale;
    if (isLtFunnel) return offersLifetime;
    if (isYtFunnel) return offersYtSale;
    if (isTwFunnel) return offersTwSale;
    return offersTrial;
  };

  const offers: any = offersOptions();
  const currentCheckout = offers[checkoutId];

  interface CheckoutPopupLinks {
    co: string;
    mx: string;
    pe: string;
    bestPrice: string;
    monthly: string;
  }

  /**
   * Generates the Hotmart checkout popup link with the provided parameters.
   * @param id - The ID of the product.
   * @param off - The off parameter value.
   * @param offDiscount - The offDiscount parameter value.
   * @returns The generated checkout popup link.
   */
  const checkoutPopupLink = (id: string, off: string, offDiscount: string) => {
    const baseUrl = `https://pay.hotmart.com/${id}`;
    const urlParams = !isPostLeadFlowStrategy ? screenData.urlParams : {};

    return (
      baseUrl +
      "?" +
      converObjectToParams({
        ...urlParams,
        off: off,
        offDiscount: offDiscount,
        sck: rudderanalytics.getAnonymousId(),
        checkoutMode: "10",
        hidePix: "0",
        hideBillet: "0"
      })
    );
  };

  const checkoutPopupLinks: CheckoutPopupLinks = {
    co: checkoutPopupLink("Y92363487F", "44sdfdbq", "promo63off"),
    mx: checkoutPopupLink("Y92363487F", "98fq4cvr", "promo63off"),
    pe: checkoutPopupLink("Y92363487F", "qmj3p7r2", "promo63off"),
    bestPrice: "https://form.typeform.com/to/EtDd1wOm#email=" + screenData.urlParams.email,
    monthly: checkoutPopupLink("Y16613490E", "eptks9rt", "")
  }

  /**
   * Retrieves the price list for a given country.
   * @param country - The country code.
   * @returns The price list object for the specified country.
   */
  const priceListByCountry = (country: string) => {
    const prices: {
      [key: string]: {
        fullPrice: string;
        discountedPrice: string;
        installmentPrice: string; 
        monthlyPrice: string;
      }
    } = {
      co: {
        fullPrice: "310.329 COP",
        discountedPrice: "114.821 COP",
        installmentPrice: "9.568 COP",
        monthlyPrice: "32.424 COP"
      },
      mx: {
        fullPrice: "1485 MXN",
        discountedPrice: "549 MXN",
        installmentPrice: "",
        monthlyPrice: "156 MXN"
      },
      pe: {
        fullPrice: "300 PEN",
        discountedPrice: "111 PEN",
        installmentPrice: "9,33 PEN",
        monthlyPrice: "32 PEN"
      },
      ve: {
        fullPrice: "77 US$",
        discountedPrice: "29 US$",
        installmentPrice: "",
        monthlyPrice: "9 US$"
      },
      py: {
        fullPrice: "589.085 PYG",
        discountedPrice: "217.964 PYG",
        installmentPrice: "",
        monthlyPrice: "61.374 PYG"
      },
      ar: {
        fullPrice: "71.869 ARS",
        discountedPrice: "26.592 ARS",
        installmentPrice: "",
        monthlyPrice: "7.500 ARS"
      },
      hn: {
        fullPrice: "1967 HNL",
        discountedPrice: "729 HNL",
        installmentPrice: "",
        monthlyPrice: "205 HNL"
      },
      cl: {
        fullPrice: "90.466 CLP",
        discountedPrice: "33.472 CLP",
        installmentPrice: "2.789 CLP",
        monthlyPrice: "9.450 CLP"
      }
    };

    return prices[country] || undefined;
  };

  const buildCheckoutLink = () => {
    const baseUrl = `https://pay.hotmart.com/${checkoutId}`;
    const urlParams = !isPostLeadFlowStrategy ? screenData.urlParams : {};

    function discount (offDiscount: string | null) {
      if (selectedPlan === "yearly") {
        return offDiscount ? offDiscount : offers.discounts.base;
      }

      if (selectedPlan === "semiannual") {
        return offDiscount ? offDiscount : offers.discounts.base;
      }

      return false;
    }

    return (
      baseUrl +
      "?" +
      converObjectToParams({
        ...urlParams,
        off: screenData.co as Co ? currentCheckout[screenData.co][selectedPlan].offerCode : currentCheckout[selectedPlan].offerCode,
        offDiscount: discount(screenData.offDiscount),
        sck: rudderanalytics.getAnonymousId(),
        checkoutMode: "10",
        hidePix: "0",
        hideBillet: exp22feature ? "1" : "0"
      })
    );
  };

  const buildNextPath = () => {
    const checkoutLink = buildCheckoutLink();
    return !isPostLeadFlowStrategy
      ? checkoutLink
      : "form?redirectUrl=" + encodeURIComponent(checkoutLink);
  };

  useEffect(() => {
    setScreenData(() => ({
      ...screenData,
      submitLabel: !esPopup && t("layout.cta_continue"),
      disabledButton: false,
      nextPath: !esPopup && buildNextPath(),
      progress: exp29feature ? 25 : 24, // change this afer exp29 ends
      step: exp29feature ? 25 : 24, // change this afer exp29 ends
      bestPrice: bestPrice
    }));
  }, [selectedPlan, bestPrice]);

  return (
    <>
      { esPopup 
        && <PopupCheckout
          checkoutLink={checkoutPopupLinks}
          modalState={modalState}
          bestPriceState={bestPriceState}
          selectedPlan={selectedPlan}
          locale={screenData.co}
        />
      }
      <div>

        <h1 className="mb-4 mt-5 text-3xl font-bold tracking-tight text-gray-900  md:text-5xl lg:text-6xl">
          {newHeadline
            ? ("Maximize o ritmo da sua evolução")
            : t("select_plan.headline-transformation")}
        </h1>

        <p>{newHeadline ? <b>12 minutos = 2 horas de leitura!</b> : t("select_plan.subheadline-transformation")}</p>

        <p className="pt-5">
          {isLtFunnel ? <TimerBox text={"Oferta por tempo limitado: 12min Vitalício com um desconto exclusivo de 93%"} /> : <TimerBox />}
        </p>
        <div>
          <div
            onClick={() => setselectedPlan("semiannual")}
            className={
              "relative mt-10 cursor-pointer border-4 text-gray-900 " +
              (selectedPlan === "semiannual"
                ? "border-green-400"
                : "border-gray-400")
            }
          >
            <span className="absolute -top-4 -right-4">
              <svg width="114" height="114" viewBox="0 0 114 114" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M26.3549 13.4096L101.058 88.1143L101.058 113.996L0.472409 13.4104L26.3549 13.4096Z" fill="#3A50AE"/>
                <path d="M37.8851 33.4999L39.0251 34.6399L36.8255 39.893L36.8857 39.9532L42.1388 37.7536L43.2788 38.8936L38.1362 44.0362L37.2423 43.1423L40.7753 39.6092L40.7276 39.5615L35.7884 41.6582L35.1205 40.9903L37.2247 36.0436L37.177 35.9959L33.6365 39.5364L32.7425 38.6425L37.8851 33.4999ZM40.1425 46.1982C39.8981 45.9538 39.7215 45.6901 39.6127 45.4072C39.5073 45.1243 39.4838 44.8397 39.5424 44.5535C39.6027 44.2689 39.7592 44.0002 40.012 43.7474C40.2296 43.5298 40.4497 43.39 40.6724 43.3281C40.895 43.2661 41.1185 43.2619 41.3428 43.3155C41.5671 43.3691 41.7881 43.4612 42.0057 43.5917C42.2267 43.7223 42.441 43.8713 42.6486 44.0387C42.898 44.2412 43.1022 44.4036 43.2613 44.5258C43.422 44.6464 43.5542 44.7183 43.658 44.7418C43.7651 44.7652 43.8622 44.7334 43.9493 44.6464L43.9643 44.6313C44.1535 44.4421 44.2439 44.2396 44.2355 44.0236C44.2272 43.8077 44.1175 43.5942 43.9066 43.3833C43.6839 43.1607 43.4588 43.0326 43.2311 42.9991C43.0051 42.9673 42.8034 42.9949 42.626 43.082L41.8978 42.1127C42.1991 41.9453 42.5054 41.86 42.8168 41.8566C43.1298 41.8516 43.437 41.9211 43.7383 42.065C44.0413 42.2073 44.3276 42.4132 44.5971 42.6827C44.7829 42.8686 44.9462 43.0753 45.0868 43.303C45.2274 43.5306 45.3236 43.7708 45.3755 44.0236C45.4291 44.2747 45.4166 44.5317 45.3379 44.7945C45.2609 45.059 45.0943 45.3193 44.8382 45.5754L42.2568 48.1568L41.383 47.2829L41.9128 46.7531L41.8827 46.723C41.7203 46.7749 41.542 46.7975 41.3478 46.7908C41.157 46.7841 40.9578 46.7339 40.7502 46.6401C40.546 46.5464 40.3434 46.3991 40.1425 46.1982ZM41.0465 45.7663C41.229 45.9488 41.4232 46.071 41.6291 46.1329C41.8367 46.1932 42.0367 46.199 42.2292 46.1505C42.4234 46.1036 42.5916 46.009 42.7339 45.8667L43.1884 45.4122C43.1365 45.4072 43.0662 45.3804 42.9775 45.3319C42.8905 45.285 42.7975 45.2272 42.6988 45.1586C42.6 45.09 42.5038 45.0205 42.41 44.9502C42.3163 44.8799 42.2351 44.8188 42.1664 44.7669C42.0124 44.6497 41.8609 44.5585 41.7119 44.4932C41.563 44.4279 41.4198 44.402 41.2826 44.4153C41.147 44.4271 41.0206 44.4915 40.9034 44.6087C40.736 44.7761 40.6707 44.9636 40.7075 45.1712C40.7444 45.3787 40.8574 45.5771 41.0465 45.7663ZM43.1709 49.0708L47.0278 45.2139L47.9368 46.1228L44.0798 49.9798L43.1709 49.0708ZM48.0322 45.1235C47.8882 44.9795 47.8121 44.8079 47.8037 44.6087C47.797 44.4078 47.8615 44.2396 47.9971 44.104C48.1343 43.9667 48.3026 43.9023 48.5018 43.9106C48.7027 43.9173 48.8751 43.9927 49.019 44.1366C49.1647 44.2823 49.24 44.4547 49.245 44.6539C49.2534 44.8531 49.189 45.0213 49.0517 45.1586C48.9161 45.2942 48.7479 45.3587 48.547 45.352C48.3494 45.3453 48.1779 45.2691 48.0322 45.1235ZM50.9004 51.1255L49.9814 50.3872C50.0417 50.2801 50.0793 50.1604 50.0944 50.0281C50.1111 49.8976 50.0936 49.7611 50.0417 49.6188C49.9898 49.4765 49.8935 49.3351 49.7529 49.1945C49.5637 49.0053 49.3637 48.8873 49.1528 48.8404C48.9435 48.7952 48.7744 48.8387 48.6455 48.971C48.5317 49.0815 48.4815 49.2137 48.4949 49.3677C48.5099 49.5234 48.5928 49.7218 48.7435 49.9628L49.2607 50.7614C49.547 51.205 49.6935 51.6009 49.7002 51.9491C49.7085 52.2989 49.5755 52.6128 49.3009 52.8907C49.0565 53.1318 48.7711 53.2749 48.4446 53.3201C48.1216 53.3653 47.7842 53.3159 47.4327 53.1719C47.0812 53.028 46.7447 52.7953 46.4233 52.4739C45.9512 52.0018 45.67 51.523 45.5796 51.0376C45.4908 50.5504 45.5963 50.1018 45.896 49.6917L46.8677 50.4927C46.7338 50.7069 46.6878 50.9237 46.7296 51.143C46.7715 51.3623 46.897 51.5766 47.1063 51.7859C47.3222 52.0018 47.5398 52.1307 47.7591 52.1726C47.9801 52.2161 48.1559 52.1726 48.2865 52.042C48.3969 51.9315 48.4455 51.7976 48.4321 51.6402C48.4204 51.4845 48.345 51.2987 48.2061 51.0828L47.6863 50.2868C47.3934 49.8398 47.2494 49.4364 47.2544 49.0765C47.2611 48.7149 47.4059 48.3943 47.6888 48.1147C47.9249 47.8753 48.1952 47.7339 48.4999 47.6904C48.8079 47.6468 49.1268 47.6945 49.4566 47.8335C49.7897 47.9724 50.1103 48.1959 50.4183 48.5039C50.8703 48.9559 51.1298 49.4079 51.1967 49.8599C51.2654 50.3136 51.1666 50.7354 50.9004 51.1255ZM49.203 57.9956L54.5063 52.6923L55.4002 53.5863L54.7624 54.2241L54.8151 54.2768C54.9558 54.2299 55.1215 54.1964 55.3123 54.1764C55.5048 54.1546 55.7116 54.1789 55.9325 54.2492C56.1552 54.3178 56.3795 54.4651 56.6055 54.6911C56.9035 54.9891 57.096 55.3339 57.183 55.7257C57.2734 56.1174 57.2408 56.5317 57.0851 56.9686C56.9328 57.4055 56.6423 57.8383 56.2138 58.2668C55.7903 58.6904 55.3609 58.9808 54.9256 59.1382C54.4904 59.2955 54.0752 59.3323 53.6802 59.2486C53.2851 59.1649 52.936 58.9716 52.6331 58.6686C52.4121 58.4476 52.2656 58.2275 52.1936 58.0082C52.1216 57.7889 52.0932 57.583 52.1082 57.3905C52.1267 57.198 52.1585 57.0306 52.2037 56.8883L52.166 56.8506L50.112 58.9046L49.203 57.9956ZM53.4692 55.5122C53.2198 55.7617 53.0365 56.0153 52.9193 56.2731C52.8038 56.5325 52.7603 56.782 52.7887 57.0214C52.8205 57.2607 52.9327 57.4767 53.1252 57.6692C53.3261 57.8701 53.5479 57.9848 53.7906 58.0132C54.035 58.04 54.2853 57.994 54.5414 57.8751C54.8009 57.7563 55.0512 57.5763 55.2922 57.3352C55.5316 57.0959 55.7099 56.8489 55.8271 56.5945C55.9443 56.34 55.9878 56.0923 55.9577 55.8512C55.9275 55.6102 55.8112 55.3883 55.6086 55.1858C55.4144 54.9916 55.1985 54.8778 54.9608 54.8443C54.7231 54.8108 54.477 54.851 54.2225 54.9648C53.9698 55.0803 53.7187 55.2628 53.4692 55.5122ZM56.6758 62.7264C56.2992 62.3498 56.0556 61.9405 55.9451 61.4985C55.8346 61.0566 55.8522 60.6105 55.9978 60.1601C56.1451 59.7115 56.418 59.288 56.8164 58.8896C57.2148 58.4911 57.6392 58.2174 58.0895 58.0685C58.5415 57.9211 58.9885 57.9027 59.4304 58.0132C59.8724 58.1237 60.2817 58.3673 60.6583 58.7439C61.035 59.1206 61.2785 59.5299 61.389 59.9718C61.4995 60.4138 61.4803 60.8599 61.3313 61.3102C61.184 61.7622 60.9111 62.1874 60.5127 62.5858C60.1143 62.9842 59.6899 63.2563 59.2396 63.4019C58.7909 63.5492 58.3457 63.5676 57.9037 63.4571C57.4618 63.3466 57.0525 63.1031 56.6758 62.7264ZM57.409 62.0032C57.6133 62.2075 57.8401 62.3221 58.0895 62.3473C58.3406 62.3707 58.5951 62.3205 58.8529 62.1966C59.1124 62.0744 59.3593 61.8961 59.5936 61.6617C59.8297 61.4257 60.0088 61.178 60.131 60.9185C60.2565 60.659 60.3076 60.4037 60.2842 60.1526C60.2607 59.9015 60.1469 59.6738 59.9427 59.4696C59.7334 59.2604 59.5024 59.1432 59.2496 59.1181C58.9985 59.0946 58.7424 59.1449 58.4812 59.2687C58.2234 59.3926 57.9765 59.5726 57.7405 59.8086C57.5061 60.043 57.327 60.289 57.2031 60.5468C57.0809 60.8063 57.0315 61.0616 57.055 61.3127C57.0818 61.5638 57.1998 61.794 57.409 62.0032ZM57.9244 66.7171L63.2277 61.4138L64.1217 62.3077L63.4839 62.9455L63.5366 62.9982C63.6772 62.9514 63.8429 62.9179 64.0338 62.8978C64.2263 62.876 64.433 62.9003 64.654 62.9706C64.8766 63.0393 65.101 63.1866 65.327 63.4126C65.6249 63.7105 65.8174 64.0554 65.9045 64.4471C65.9949 64.8388 65.9622 65.2532 65.8066 65.6901C65.6542 66.127 65.3638 66.5597 64.9352 66.9883C64.5117 67.4118 64.0823 67.7022 63.6471 67.8596C63.2118 68.017 62.7967 68.0538 62.4016 67.9701C62.0065 67.8864 61.6575 67.693 61.3545 67.39C61.1335 67.1691 60.987 66.9489 60.9151 66.7296C60.8431 66.5103 60.8146 66.3044 60.8297 66.1119C60.8481 65.9194 60.8799 65.752 60.9251 65.6097L60.8874 65.5721L58.8334 67.6261L57.9244 66.7171ZM62.1907 64.2337C61.9412 64.4831 61.7579 64.7367 61.6408 64.9945C61.5252 65.254 61.4817 65.5034 61.5102 65.7428C61.542 65.9822 61.6541 66.1981 61.8467 66.3906C62.0475 66.5915 62.2693 66.7062 62.5121 66.7347C62.7565 66.7614 63.0068 66.7154 63.2629 66.5966C63.5224 66.4777 63.7726 66.2977 64.0137 66.0567C64.2531 65.8173 64.4313 65.5704 64.5485 65.3159C64.6657 65.0615 64.7092 64.8137 64.6791 64.5727C64.649 64.3316 64.5326 64.1098 64.3301 63.9072C64.1359 63.7131 63.9199 63.5992 63.6822 63.5657C63.4445 63.5323 63.1984 63.5724 62.944 63.6863C62.6912 63.8018 62.4401 63.9842 62.1907 64.2337ZM67.8706 70.5263L70.1054 68.2915L71.0144 69.2005L67.1575 73.0574L66.2761 72.1761L66.9616 71.4906L66.9215 71.4504C66.6185 71.5793 66.2987 71.6128 65.9622 71.5508C65.6274 71.4906 65.3152 71.3156 65.0256 71.026C64.7728 70.7732 64.6054 70.4937 64.5234 70.1873C64.4447 69.881 64.459 69.5654 64.5661 69.2407C64.6749 68.9142 64.8833 68.597 65.1914 68.289L67.6471 65.8332L68.5561 66.7422L66.241 69.0574C65.9966 69.3018 65.8693 69.5629 65.8593 69.8408C65.8492 70.1187 65.953 70.3665 66.1707 70.5841C66.3046 70.718 66.467 70.8151 66.6578 70.8754C66.8486 70.9356 67.0487 70.9415 67.2579 70.8929C67.4705 70.8444 67.6748 70.7222 67.8706 70.5263ZM74.1438 69.7586L69.0012 74.9012L68.0922 73.9922L73.2348 68.8496L74.1438 69.7586ZM70.968 77.0236C70.7236 76.7792 70.547 76.5156 70.4382 76.2326C70.3327 75.9497 70.3093 75.6651 70.3678 75.3789C70.4281 75.0943 70.5846 74.8256 70.8374 74.5728C71.055 74.3552 71.2752 74.2154 71.4978 74.1535C71.7205 74.0916 71.9439 74.0874 72.1683 74.141C72.3926 74.1945 72.6135 74.2866 72.8312 74.4172C73.0521 74.5477 73.2664 74.6967 73.474 74.8641C73.7234 75.0667 73.9277 75.2291 74.0867 75.3513C74.2474 75.4718 74.3796 75.5438 74.4834 75.5672C74.5906 75.5907 74.6877 75.5588 74.7747 75.4718L74.7898 75.4567C74.9789 75.2676 75.0693 75.065 75.061 74.8491C75.0526 74.6331 74.9429 74.4197 74.732 74.2087C74.5094 73.9861 74.2842 73.858 74.0566 73.8246C73.8306 73.7928 73.6288 73.8204 73.4514 73.9074L72.7232 72.9382C73.0245 72.7708 73.3309 72.6854 73.6422 72.682C73.9553 72.677 74.2625 72.7465 74.5638 72.8905C74.8668 73.0327 75.153 73.2387 75.4226 73.5082C75.6084 73.694 75.7716 73.9007 75.9122 74.1284C76.0528 74.3561 76.1491 74.5963 76.201 74.8491C76.2545 75.1002 76.242 75.3571 76.1633 75.6199C76.0863 75.8844 75.9197 76.1448 75.6636 76.4009L73.0823 78.9822L72.2084 78.1084L72.7383 77.5786L72.7081 77.5484C72.5458 77.6003 72.3675 77.6229 72.1733 77.6162C71.9824 77.6095 71.7832 77.5593 71.5757 77.4656C71.3714 77.3718 71.1689 77.2245 70.968 77.0236ZM71.872 76.5917C72.0544 76.7742 72.2486 76.8964 72.4545 76.9583C72.6621 77.0186 72.8621 77.0245 73.0547 76.9759C73.2488 76.929 73.4171 76.8345 73.5594 76.6922L74.0139 76.2377C73.962 76.2326 73.8917 76.2059 73.8029 76.1573C73.7159 76.1104 73.623 76.0527 73.5242 75.984C73.4254 75.9154 73.3292 75.8459 73.2354 75.7756C73.1417 75.7053 73.0605 75.6442 72.9919 75.5923C72.8379 75.4751 72.6864 75.3839 72.5374 75.3186C72.3884 75.2533 72.2453 75.2274 72.108 75.2408C71.9724 75.2525 71.846 75.317 71.7288 75.4341C71.5614 75.6015 71.4961 75.789 71.533 75.9966C71.5698 76.2042 71.6828 76.4026 71.872 76.5917ZM73.9963 79.8962L77.8532 76.0393L78.7346 76.9207L78.0918 77.5635L78.132 77.6037C78.4249 77.4513 78.717 77.4003 79.0083 77.4505C79.3029 77.5007 79.5607 77.6363 79.7817 77.8573C79.8319 77.9075 79.8855 77.9661 79.9424 78.0331C80.0027 78.1 80.0496 78.1586 80.083 78.2088L79.2469 79.045C79.2217 78.9931 79.1724 78.9203 79.0987 78.8265C79.0284 78.7328 78.9547 78.6474 78.8777 78.5704C78.712 78.4047 78.527 78.2917 78.3228 78.2314C78.1219 78.1712 77.921 78.1661 77.7202 78.2164C77.5193 78.2666 77.3368 78.3737 77.1727 78.5378L74.9053 80.8052L73.9963 79.8962Z" fill="#E9EAF9"/>
              </svg>
            </span>

            <div className="flex bg-gray-100 p-4 py-6 text-left">
              <div className="pr-4">
                <div className="mt-1.5 rounded-full border-2 border-gray-900 bg-white p-0.5">
                  <div
                    className={
                      "h-2 w-2 rounded-full  bg-gray-900 " +
                      (selectedPlan === "semiannual" ? "bg-gray-900" : "bg-white")
                    }
                  ></div>
                </div>
              </div>
              <div>
                <div className="-ml-11 -mt-10 mb-2 w-28  bg-red-600 py-1 text-center text-xs text-white">
                  {t(
                    `select_plan.semiannual.save_offer`
                  )}
                </div>
                <strong className="mt-5 block text-sm">
                  {t("select_plan.semiannual.plan_name")}
                </strong>
                <div className="py-2">
                  <strong className="block text-xs font-normal text-red-600 line-through">
                    {t("select_plan.semiannual.plan_price_per_month_without_discount")}
                  </strong>

                  {bestPrice && (
                    <strong className="block text-sm font-bold text-red-600 line-through">
                    {}
                    </strong>
                  )}

                  <b className="block">
                    {t("select_plan.semiannual.plan_price_per_month")}
                  </b>
                </div>
                <b className="block text-sm text-blue-700">
                  {t(
                    `select_plan.yearly.${transPrefix}free_trial`
                  )}
                </b>
              </div>
            </div>
            <div className="ml-8 p-4 text-left">
              <b className="text-sm">
                {t(
                  `select_plan.yearly.${transPrefix}how_trial_works`
                )}
                :
              </b>
              <div className="mt-2 text-left text-sm">
                <div className="mb-3">
                  <b className="text-blue-700">
                    {t("book_list.trial_box.today.headline")}
                  </b>
                  <p className="font-light">
                    {t(
                      `book_list.trial_box.today.${
                        (isDirectSale || isRetFunnel || isLtFunnel || isTwFunnel || isYtFunnel) ? "direct_sale_" : ""
                      }text`
                    )}
                  </p>
                </div>
                <div className="mb-3">
                  <b className="text-blue-700">
                    {t("book_list.trial_box.day5.headline")}
                  </b>
                  <p className="font-light">
                    {t(
                      `book_list.trial_box.day5.${
                        (isDirectSale || isRetFunnel || isLtFunnel || isTwFunnel || isYtFunnel) ? "direct_sale_" : ""
                      }text`
                    )}
                  </p>
                </div>
                <div className="mb-3">
                  <b className="text-blue-700">
                    {t("book_list.trial_box.day7.headline")}
                  </b>
                  <p className="font-light">
                    {t("book_list.trial_box.day7.text")}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {(!removeMonthly && !isLtFunnel) && (
            <div
              className="py-3 text-sm text-blue-600"
              onClick={() => setShowOthersPlans(!showOthersPlans)}
            >
              <span className="cursor-pointer">
                {t("select_plan.show_others_plans")}
              </span>
            </div>
          )}

          {showOthersPlans && (
            <>
              <div
                onClick={() => setselectedPlan("yearly")}
                className={
                  "cursor-pointer border-4  text-gray-900 my-4 " +
                  (selectedPlan === "yearly"
                    ? "border-green-400"
                    : "border-gray-400")
                }
              >
                <div className="flex bg-gray-100 p-4 py-6 text-left">
                  <div className="pr-4">
                    <div className="mt-0.5 rounded-full border-2 border-gray-900 bg-white p-0.5">
                      <div
                        className={
                          "h-2 w-2 rounded-full  bg-gray-900 " +
                          (selectedPlan === "yearly"
                            ? "bg-gray-900"
                            : "bg-white")
                        }
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="-ml-11 -mt-10 mb-2 w-28  bg-red-600 py-1 text-center text-xs text-white">
                      {t(
                        `select_plan.yearly.save_offer`
                      )}
                    </div>
                    <strong className="block text-sm">
                      {t("select_plan.yearly.plan_name")}
                    </strong>
                    <div className="py-2">
                      <strong className="block text-xs font-normal text-red-600 line-through">
                        {t("select_plan.yearly.plan_price_per_month_without_discount")}
                      </strong>
                      <b className="block">
                        {t("select_plan.yearly.plan_price_per_month")}
                      </b>
                    </div>
                    <b className="block text-sm text-blue-700">
                      {t(
                        `select_plan.monthly.${
                          (isDirectSale || isRetFunnel) ? "direct_sale_" : ""
                        }no_trial`
                      )}
                    </b>
                  </div>
                </div>
              </div>

              <div
                onClick={() => setselectedPlan("monthly")}
                className={
                  "cursor-pointer border-4  text-gray-900 " +
                  (selectedPlan === "monthly"
                    ? "border-green-400"
                    : "border-gray-400")
                }
                >
                <div className="flex bg-gray-100 p-4 py-6 text-left">
                  <div className="pr-4">
                    <div className="mt-0.5 rounded-full border-2 border-gray-900 bg-white p-0.5">
                      <div
                        className={
                          "h-2 w-2 rounded-full  bg-gray-900 " +
                          (selectedPlan === "monthly"
                            ? "bg-gray-900"
                            : "bg-white")
                        }
                      ></div>
                    </div>
                  </div>
                  <div>
                    <b className="block text-sm">
                      {t("select_plan.monthly.plan_name")}
                    </b>
                    <div className="py-2">
                      <b className="block">
                        {(screenData.co && priceListByCountry(screenData.co)) ? (
                          priceListByCountry(screenData.co).monthlyPrice
                        ) : (
                          t(
                            `select_plan.monthly.${transPrefix}plan_price_per_month${
                              isWithInterestVariant ? "_with_interest" : ""
                            }`
                          )
                        )}
                      </b>
                    </div>
                    <b className="block text-sm text-blue-700">
                      {t(
                        `select_plan.monthly.${
                          (isDirectSale || isRetFunnel) ? "direct_sale_" : ""
                        }no_trial`
                      )}
                    </b>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {variant === "select-plan-green-box" && (
        <div>
          <div className="my-5 rounded bg-green-200 px-5 pt-1 pb-4">
            <h2 className="mb-4 mt-5 text-xl font-bold tracking-tight text-gray-900  md:text-2xl lg:text-3xl">
              {t("select_plan.green_box.headline")}
            </h2>
            <div className="text-left">
              <p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mb-1 mr-1 inline-block h-5 w-5 text-green-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                  />
                </svg>
                {t("select_plan.green_box.item1")}
              </p>
              <p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mb-1 mr-1 inline-block h-5 w-5 text-green-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                  />
                </svg>
                {t("select_plan.green_box.item2")}
              </p>
              <p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mb-1 mr-1 inline-block h-5 w-5 text-green-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                  />
                </svg>
                {t("select_plan.green_box.item3")}
              </p>
              <p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mb-1 mr-1 inline-block h-5 w-5 text-green-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                  />
                </svg>
                {t("select_plan.green_box.item4")}
              </p>
              <p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mb-1 mr-1 inline-block h-5 w-5 text-green-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                  />
                </svg>
                {t("select_plan.green_box.item5")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FAQ */}
      <div>
        <h2 className="mb-4 mt-5 text-2xl font-bold tracking-tight text-gray-900  md:text-3xl lg:text-4xl">
          {t("select_plan.faq.headline")}
        </h2>

        <div className="text-left">
          <div className="border-b border-gray-500">
            <div
              className="flex cursor-pointer"
              onClick={() => setCurrentFaq(1)}
            >
              <div className="mt-3 pr-3">
                {currentFaq === 1 ? <CloseIcon /> : <OpenIcon />}
              </div>
              <div>
                <h3 className="py-3 font-bold">
                  {t("select_plan.faq.q1.question")}
                </h3>
              </div>
            </div>
            {currentFaq === 1 && (
              <div className="pb-3">
                <p className="text-sm font-light text-gray-700 ">
                  {t("select_plan.faq.q1.answer")}
                </p>
              </div>
            )}
          </div>

          <div className="border-b border-gray-500">
            <div
              className="flex cursor-pointer"
              onClick={() => setCurrentFaq(2)}
            >
              <div className="mt-3 pr-3">
                {currentFaq === 2 ? <CloseIcon /> : <OpenIcon />}
              </div>
              <div>
                <h3 className="py-3 font-bold">
                  {t(
                    `select_plan.faq.${
                      (isDirectSale || isRetFunnel || isLtFunnel || isTwFunnel || isYtFunnel) ? "direct_sale_" : ""
                    }q2.question`
                  )}
                </h3>
              </div>
            </div>
            {currentFaq === 2 && (
              <div className="pb-3">
                <p className="text-sm font-light text-gray-700 ">
                  {t(
                    `select_plan.faq.${
                      (isDirectSale || isRetFunnel || isLtFunnel || isTwFunnel || isYtFunnel) ? "direct_sale_" : ""
                    }q2.answer`
                  )}
                </p>
              </div>
            )}
          </div>
          
          {!isLtFunnel && (
          <div className="border-b border-gray-500">
            <div
              className="flex cursor-pointer"
              onClick={() => setCurrentFaq(3)}
            >
              <div className="mt-3 pr-3">
                {currentFaq === 3 ? <CloseIcon /> : <OpenIcon />}
              </div>
              <div>
                <h3 className="py-3 font-bold">
                  {t("select_plan.faq.q3.question")}
                </h3>
              </div>
            </div>
            {currentFaq === 3 && (
              <div className="pb-3">
                <p className="text-sm font-light text-gray-700 ">
                  {t("select_plan.faq.q3.answer")}
                </p>
              </div>
            )}
          </div>
          )}

          <div className="border-b border-gray-500">
            <div
              className="flex cursor-pointer"
              onClick={() => setCurrentFaq(4)}
            >
              <div className="mt-3 pr-3">
                {currentFaq === 4 ? <CloseIcon /> : <OpenIcon />}
              </div>
              <div>
                <h3 className="py-3 font-bold">
                  {t(
                    `select_plan.faq.${
                      (isLtFunnel) ? "lifetime_" : ""
                    }q4.question`
                  )}
                </h3>
              </div>
            </div>
            {currentFaq === 4 && (
              <div className="pb-3">
                <p className="text-sm font-light text-gray-700 ">
                  {t("select_plan.faq.q4.answer")}
                </p>
              </div>
            )}
          </div>

          <div className="border-b border-gray-500">
            <div
              className="flex cursor-pointer"
              onClick={() => setCurrentFaq(5)}
            >
              <div className="mt-3 pr-3">
                {currentFaq === 5 ? <CloseIcon /> : <OpenIcon />}
              </div>
              <div>
                <h3 className="py-3 font-bold">
                  {t(
                    `select_plan.faq.${
                      (isDirectSale || isRetFunnel || isTwFunnel || isYtFunnel) 
                        ? "direct_sale_" 
                        : isLtFunnel ? "lifetime_" : ""
                    }q5.question`
                  )}
                </h3>
              </div>
            </div>
            {currentFaq === 5 && (
              <div className="pb-3">
                <p className="text-sm font-light text-gray-700 ">
                  {t(
                    `select_plan.faq.${
                      (isDirectSale || isRetFunnel || isTwFunnel || isYtFunnel) 
                        ? "direct_sale_" 
                        : isLtFunnel ? "lifetime_" : ""
                    }q5.answer`
                  )}
                </p>
              </div>
            )}
          </div>

          <div className="border-gray-500 pb-20">
            <div
              className="flex cursor-pointer"
              onClick={() => setCurrentFaq(6)}
            >
              <div className="mt-3 pr-3">
                {currentFaq === 6 ? <CloseIcon /> : <OpenIcon />}
              </div>
              <div>
                <h3 className="py-3 font-bold">
                  {t("select_plan.faq.q6.question")}
                </h3>
              </div>
            </div>
            {currentFaq === 6 && (
              <div className="pb-3">
                <p className="text-sm font-light text-gray-700 ">
                  {t("select_plan.faq.q6.answer")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {esPopup && (
        <div className="fixed inset-x-0 bottom-0 mx-auto max-w-screen-sm bg-gradient-to-t from-white via-white">
          <div className="mx-4">
            <button
              onClick={() => setShowModal(true)}
              className="hotmart-fb flex justify-center text-center text-md my-6 w-full rounded-md bg-blue-700 px-5 py-2.5 font-normal text-white hover:bg-blue-800 focus:ring-blue-300"
            >Continuar</button>
          </div>
        </div>
      )}
    </>
  );
}

export default SelectPlan;
